import { RemoveRedEye } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Tooltip,
  Typography,
} from '@mui/material';
import { fetchCreditLineEvaluation } from 'lib/api/decisionEngine';
import { EvaluationDetail } from 'lib/api/decisionEngine/types';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { Evaluation, EvaluationNode } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import CustomButton from '../../../../../../lib/designSystem/Button';
import { formatDateToLocal } from '../../../../../../lib/designSystem/Table/cellFormat';
import { LightTooltip } from '../../../../../../lib/designSystem/WhiteTooltip';

export const ENGINE_TYPES: Record<string, string> = {
  tree: 'Árbol',
  matrix: 'Matriz',
  '': 'Indefinido',
};

const EvaluationInformation = (props: { evaluation?: Evaluation }) => {
  const { evaluation } = props;
  if (!evaluation) return;
  return (
    <div>
      <Typography variant="body1" fontWeight="bold" margin="8px 0px">
        {' '}
        Informe de la evaluación
      </Typography>

      <div>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <Typography>
              <b>Nodo</b>
            </Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography>
              <b>Decisión:</b>
            </Typography>
          </Grid>
          {evaluation.engine.engineType !== 'Árbol' ? (
            <Grid item xs={12} md={3}>
              <Typography>
                <b>Resultado:</b>
              </Typography>
            </Grid>
          ) : (
            <Grid item xs={2} />
          )}
          <Grid
            item
            xs={12}
            md={evaluation.engine.engineType !== 'Árbol' ? 4 : 5}
          >
            <Typography>
              <b>Detalles:</b>
            </Typography>
          </Grid>
        </Grid>
        {evaluation.result.map((result, index: number) => (
          <ResultsDetails
            result={result}
            index={index}
            key={index}
            engineType={evaluation.engine.engineType}
          />
        ))}
      </div>
    </div>
  );
};

const colorFullDecision = (text: string) => {
  switch (text) {
    case 'Aprobado':
      // return <Chip label={text} color="success" />;
      return (
        <Typography style={{ color: 'green', fontWeight: 'bold' }}>
          {text}
        </Typography>
      );
    case 'Rechazado':
      // return <Chip label={text} color="error" />;
      return (
        <Typography style={{ color: 'red', fontWeight: 'bold' }}>
          {text}
        </Typography>
      );
    default:
      return (
        <Typography style={{ color: '#E8C547', fontWeight: 'bold' }}>
          {text}
        </Typography>
      );
    // return (
    //   <Chip
    //     label={text}
    //     style={{ backgroundColor: '#E8C547', color: 'white' }}
    //   />
    // );
  }
};

const ResultsDetails = (props: {
  result: EvaluationNode;
  index: number;
  engineType: string;
}) => {
  const { result, index, engineType } = props;
  return (
    <div>
      <Grid container spacing={2} alignItems={'center'}>
        <Grid item xs={12} md={2}>
          <Typography>{index}</Typography>
        </Grid>
        <Grid item xs={12} md={3}>
          <Tooltip title={result.resultText}>
            <div style={{ padding: '5px' }}>
              <Typography>{result.nodeName}</Typography>
            </div>
          </Tooltip>
        </Grid>
        {engineType !== 'Árbol' ? (
          <Grid item xs={12} md={3}>
            {colorFullDecision(result.statusNode.name)}
          </Grid>
        ) : (
          <Grid item xs={2} />
        )}
        <Grid item xs={12} md={engineType !== 'Árbol' ? 4 : 5}>
          {result.subDecisionEngineRevisionStory[0] ? (
            <LightTooltip
              title={
                <div style={{ padding: '10px', minWidth: '700px' }}>
                  <Typography fontWeight="bold">
                    {colorFullDecision(result.statusNode.name)}
                  </Typography>
                  {/* <EvaluationInformation evaluation={} /> */}
                </div>
              }
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'blue',
                  cursor: 'pointer',
                }}
              >
                <RemoveRedEye style={{ marginRight: '5px' }} />
                <Typography fontWeight="bold">SubEvaluación</Typography>
              </div>
            </LightTooltip>
          ) : (
            <Typography>{result.resultText}</Typography>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

const EvaluationDetails = (props: {
  evaluationId: number;
  open: boolean;
  setOpen: (bool: boolean) => void;
}) => {
  const { evaluationId, open, setOpen } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [evaluation, setEvaluation] = useState<EvaluationDetail>();

  const handleDetails = async () => {
    try {
      setLoading(true);
      const evaluation = await fetchCreditLineEvaluation(
        dispatch,
        evaluationId,
      );
      if (!evaluation) return;
      console.log('evaluation detail:', evaluation);
      setEvaluation(evaluation);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    void handleDetails();
  }, []);

  if (loading) return <CenteredCircularProgress />;

  return (
    <Dialog maxWidth="lg" fullWidth open={open} onClose={() => setOpen(false)}>
      <DialogTitle>Detalles de la evaluación</DialogTitle>
      <DialogContent>
        <div style={{ padding: '0px 0px 10px' }}>
          <Typography variant="body1" fontWeight="bold" margin="8px 0px">
            Información general
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography>
                <b>Cliente:</b>
              </Typography>
              <Typography>
                {evaluation?.businessName} ({evaluation?.businessIdentifier})
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography>
                <b>Resultado:</b>
              </Typography>
              <Typography>
                {colorFullDecision(evaluation?.statusNode.name ?? '')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography>
                <b>Descripción:</b>
              </Typography>
              <Typography>{evaluation?.statusNode.description}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography>
                <b>Nombre motor:</b>
              </Typography>
              <Typography>{evaluation?.engine.name}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography>
                <b>Tipo motor:</b>
              </Typography>
              <Typography>
                {ENGINE_TYPES[evaluation?.engine.engineType ?? '']}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography>
                <b>Fecha de evaluación:</b>
              </Typography>
              <Typography>
                {formatDateToLocal(evaluation?.createdAt, false)}
              </Typography>
            </Grid>
          </Grid>
        </div>
        <Divider />
        <EvaluationInformation evaluation={evaluation} />
      </DialogContent>
      <DialogActions>
        <CustomButton color="secondary" onClick={() => setOpen(false)}>
          {' '}
          Salir{' '}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default EvaluationDetails;
